import axios, { AxiosRequestConfig, AxiosError } from "axios";

declare module "axios" {
  interface AxiosRequestConfig {
    /** On a 401 whose refresh fails, reject without navigating to /login. */
    skipLoginRedirect?: boolean;
  }
}

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || "/api",
});

/**
 * Mirror the access token into a short-lived cookie so /api/docs (a
 * top-level browser navigation, no Authorization header available) can
 * still verify the user's role. Kept separate from localStorage so the
 * SPA's own requests don't pick it up by accident.
 */
export const setDocsCookie = (accessToken: string) => {
  const secure = window.location.protocol === "https:" ? "; Secure" : "";
  document.cookie = `woxa.docs=${encodeURIComponent(accessToken)}; Path=/; Max-Age=86400; SameSite=Lax${secure}`;
};

export const clearDocsCookie = () => {
  document.cookie = "woxa.docs=; Path=/; Max-Age=0; SameSite=Lax";
};

const PUBLIC_PATHS = ["/create", "/forgot-password", "/reset-password"];

const clearSession = () => {
  window.localStorage.removeItem("access_token");
  window.localStorage.removeItem("refresh_token");
  clearDocsCookie();
};

const redirectToLogin = () => {
  if (!PUBLIC_PATHS.includes(window.location.pathname)) {
    window.location.href = "/login";
  }
};

/**
 * Single-flight refresh. Refresh tokens rotate and are single-use on the
 * server (re-use = theft → all sessions revoked), so concurrent 401s must
 * share ONE POST /auth/refresh-token. Every caller awaits the same promise.
 * On failure the session is cleared exactly once and the promise rejects.
 */
let refreshInFlight: Promise<string> | null = null;

export const refreshAccessToken = (): Promise<string> => {
  if (refreshInFlight) return refreshInFlight;
  const refreshToken = window.localStorage.getItem("refresh_token");
  if (!refreshToken) {
    clearSession();
    return Promise.reject(new Error("No refresh token"));
  }
  refreshInFlight = (async () => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL || "/api"}/auth/refresh-token`,
        { refreshToken }
      );
      const { accessToken, refreshToken: newRefreshToken } = response.data;
      window.localStorage.setItem("access_token", accessToken);
      window.localStorage.setItem("refresh_token", newRefreshToken);
      setDocsCookie(accessToken);
      return accessToken as string;
    } catch (refreshError) {
      clearSession();
      throw refreshError;
    } finally {
      refreshInFlight = null;
    }
  })();
  return refreshInFlight;
};

api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config;
    // Skip refresh-on-401 for /auth/* endpoints. A 401 from /auth/login or
    // /auth/register means "wrong credentials" — refreshing wouldn't help and
    // the silent page reload below would wipe the form's "Invalid email or
    // password" message before the user could read it.
    const isAuthEndpoint = !!originalRequest?.url?.match(/\/auth\//)
    if (
      error.response?.status === 401 &&
      originalRequest &&
      !(originalRequest as any)._retry &&
      !isAuthEndpoint
    ) {
      (originalRequest as any)._retry = true;
      // If another request already refreshed while this one was in flight,
      // just retry with the current token instead of rotating again.
      const sentAuth = originalRequest.headers?.Authorization;
      const current = window.localStorage.getItem("access_token");
      if (current && typeof sentAuth === "string" && sentAuth !== `Bearer ${current}`) {
        originalRequest.headers.Authorization = `Bearer ${current}`;
        return api(originalRequest);
      }
      const hadRefreshToken = !!window.localStorage.getItem("refresh_token");
      let accessToken: string;
      try {
        accessToken = await refreshAccessToken();
      } catch (refreshError) {
        // Opt-out for non-critical calls (mini game scores) that must never leave the page.
        if (!originalRequest.skipLoginRedirect) redirectToLogin();
        return Promise.reject(hadRefreshToken ? refreshError : error);
      }
      if (originalRequest.headers) {
        originalRequest.headers.Authorization = `Bearer ${accessToken}`;
      }
      return api(originalRequest);
    }
    return Promise.reject(error);
  }
);

export default api;

export const getHeaders = () => {
  const accessToken = window.localStorage.getItem("access_token");
  return {
    headers: {
      Authorization: "Bearer " + accessToken,
    },
  };
};

export const setLocalStorage = (key: String, values: string) => {
  window.localStorage.setItem(`${key}`, `${values}`);
};

export const getLocalStorage = (key: string) => {
  const getLocal = window.localStorage.getItem(`${key}`);
  return {
    now_token: getLocal,
  };
};

export const configHeader = () => {
  const accessToken = window.localStorage.getItem("access_token");
  const headers = {
    accept: "application/json",
    "Content-Type": "application/json",
    Authorization: "Bearer " + accessToken,
  };
  const config: AxiosRequestConfig = {
    headers: headers,
  };
  return config;
};
